// src/components/Form/SearchableSelect.js
import React, { useEffect, useMemo, useState } from 'react';
import { normalizeTextForSearch } from '../../utils/stringUtils';
import './SearchableSelect.css';

const SearchableSelect = ({
  id,
  value,
  options = [],
  onChange,
  disabled = false,
  placeholder = 'Selecione uma opção',
  emptyMessage = 'Nenhuma opção encontrada',
  inputClassName = '',
}) => {
  const listboxId = `${id}-listbox`;
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hasTyped, setHasTyped] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const selectedOption = options.find((option) => String(option.value) === String(value)) || null;
  const filteredOptions = useMemo(() => {
    if (!hasTyped) return options;
    const normalizedQuery = normalizeTextForSearch(query);
    return options.filter((option) => normalizeTextForSearch(option.label).includes(normalizedQuery));
  }, [hasTyped, options, query]);

  useEffect(() => {
    if (!hasTyped) setQuery(selectedOption?.label || '');
  }, [hasTyped, selectedOption?.label]);

  const closeList = () => {
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const openList = () => {
    if (disabled) return;
    setIsOpen(true);
    setHasTyped(false);
    const selectedIndex = options.findIndex((option) => String(option.value) === String(value));
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : (options.length > 0 ? 0 : -1));
  };

  const handleFocus = (event) => {
    openList();
    event.currentTarget.select();
  };

  const handleInputChange = (event) => {
    setQuery(event.target.value);
    setHasTyped(true);
    setIsOpen(true);
    setActiveIndex(0);
    if (value !== null && value !== undefined && value !== '') onChange(null);
  };

  const handleSelect = (option) => {
    setQuery(option.label);
    setHasTyped(false);
    closeList();
    onChange(option.value);
  };

  const handleKeyDown = (event) => {
    if (disabled) return;

    if (event.key === 'Escape') {
      closeList();
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!isOpen) {
        openList();
        return;
      }
      if (filteredOptions.length === 0) return;
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((current) => {
        if (current < 0) return direction > 0 ? 0 : filteredOptions.length - 1;
        return (current + direction + filteredOptions.length) % filteredOptions.length;
      });
      return;
    }

    if (event.key === 'Enter' && isOpen && activeIndex >= 0 && filteredOptions[activeIndex]) {
      event.preventDefault();
      handleSelect(filteredOptions[activeIndex]);
    }
  };

  const handleBlur = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) closeList();
  };

  return (
    <div className="searchable-select" onBlur={handleBlur}>
      <input
        id={id}
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-activedescendant={isOpen && activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined}
        autoComplete="off"
        className={inputClassName}
        value={query}
        placeholder={placeholder}
        disabled={disabled}
        onFocus={handleFocus}
        onClick={() => {
          if (!isOpen) openList();
        }}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
      />
      {isOpen && !disabled && (
        <div id={listboxId} className="searchable-select-listbox" role="listbox">
          {filteredOptions.length > 0 ? filteredOptions.map((option, index) => (
            <button
              key={option.value}
              id={`${listboxId}-option-${index}`}
              type="button"
              role="option"
              aria-selected={String(option.value) === String(value)}
              className={`searchable-select-option${index === activeIndex ? ' active' : ''}`}
              tabIndex={-1}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => handleSelect(option)}
            >
              {option.label}
            </button>
          )) : (
            <div className="searchable-select-empty" role="status">{emptyMessage}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
